import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: "Missing session_id." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // The status endpoint intentionally only returns session_id/status/payment_status.
          // Pull the active plan name from the authenticated user profile.
          let planName = null;
          try {
            const me = await api.get("/auth/me");
            planName = me.data?.subscription?.plan_name || null;
          } catch { /* unauthenticated success page is still a valid state */ }
          setState({ phase: "paid", data: { plan_name: planName } });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ phase: "failed", data });
          return;
        }
      } catch (e) {
        if (attemptsRef.current > 10) {
          setState({ phase: "error", data: "We couldn't confirm payment status. Please check back in a moment." });
          return;
        }
      }
      if (attemptsRef.current > 20) {
        setState({ phase: "slow", data: null });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[hsl(var(--navy))] text-slate-100 flex items-center justify-center px-6">
      <div className="max-w-md w-full rounded-lg border border-white/10 bg-[hsl(var(--navy-muted))]/70 p-8 text-center">
        {state.phase === "polling" && (
          <>
            <Loader2 size={32} className="mx-auto text-[hsl(var(--gold))] animate-spin"/>
            <h1 className="text-2xl font-semibold tracking-tight mt-5">Confirming your payment…</h1>
            <p className="text-sm text-slate-400 mt-2">Stripe is finalising the transaction. This usually takes a few seconds.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 size={32} className="mx-auto text-emerald-400"/>
            <h1 className="text-2xl font-semibold tracking-tight mt-5" data-testid="payment-success-heading">Payment confirmed</h1>
            <p className="text-sm text-slate-400 mt-2">
              {state.data?.plan_name
                ? <>Welcome to the <span className="text-white font-medium">{state.data.plan_name}</span> plan. Your subscription is now active.</>
                : <>Your subscription is now active.</>}
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <button onClick={() => nav("/buyer")} className="btn-gold w-full" data-testid="go-to-dashboard-btn">
                Go to dashboard <ArrowRight size={14}/>
              </button>
              <Link to="/pricing" className="text-xs text-slate-500 hover:text-slate-300">View plan details</Link>
            </div>
          </>
        )}
        {state.phase === "failed" && (
          <>
            <XCircle size={32} className="mx-auto text-rose-400"/>
            <h1 className="text-2xl font-semibold tracking-tight mt-5">Payment not completed</h1>
            <p className="text-sm text-slate-400 mt-2">The transaction didn't complete. No charge has been made.</p>
            <Link to="/pricing" className="btn-gold mt-6 w-full">Back to pricing</Link>
          </>
        )}
        {state.phase === "slow" && (
          <>
            <Loader2 size={32} className="mx-auto text-[hsl(var(--gold))]"/>
            <h1 className="text-2xl font-semibold tracking-tight mt-5">Still processing</h1>
            <p className="text-sm text-slate-400 mt-2">This is taking a little longer than usual. You can safely close this tab — we'll email you once it's confirmed.</p>
            <Link to="/buyer" className="btn-gold mt-6 w-full">Open dashboard</Link>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle size={32} className="mx-auto text-rose-400"/>
            <h1 className="text-2xl font-semibold tracking-tight mt-5">Something went wrong</h1>
            <p className="text-sm text-slate-400 mt-2">{state.data}</p>
            <Link to="/pricing" className="btn-gold mt-6 w-full">Back to pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
